/**
 * AdScore integration — invalid-traffic (IVT) detection.
 *
 * Loads AdScore's JavaScript API tag (`//c.adsco.re`) at SDK init, calls
 * `AdscoreInit(apiKey, { sub_id, async_callback: 1, callback })` to obtain
 * an opaque **signature**, then round-trips the signature to the GetJar
 * backend (`POST /ads/adscore/verify`) which decodes it into a verdict
 * (`ok` | `junk` | `proxy` | `bot`) using the server-only validation key.
 *
 * The verdict is verified **once per session** and cached server-side; every
 * impression in the session reuses the cached verdict. Nothing on the ad hot
 * path talks to AdScore or the backend after `loadAndWait()` completes.
 *
 * **Activation is controlled by the GetJar backend**, not the partner. The
 * backend includes an `adscore` block in the `/ads/init` response when
 * AdScore should run for this session; absence (or empty `apiKey`) means
 * it's off. Partners cannot opt in or out — IVT filtering is a uniform
 * GetJar policy.
 *
 * Fail-open by design: if the AdScore script fails to load, the JS API
 * times out, the callback errors, or the backend verify fails, we behave
 * as if AdScore were disabled. We never let a third-party IVT outage deny
 * real users their ads.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const ADSCORE_SCRIPT_URL = 'https://c.adsco.re';
const DEFAULT_SUB_ID = 'getjar-sdk';
// AdScore's own verdict enum (from server-side verify). We block `junk` too by
// default alongside `proxy` and `bot` — legit traffic returns `ok`.
const DEFAULT_BLOCKED_VERDICTS = ['proxy', 'bot', 'junk'];
const DEFAULT_TIMEOUT_MS = 3000;
export class AdScoreClient {
    constructor() {
        this.verdict = null;
        this.loaded = false;
        this.effectiveConfig = null;
    }
    /**
     * Initialise AdScore for this session:
     *   1. Inject the `//c.adsco.re` script.
     *   2. Call `AdscoreInit` with our zone `apiKey` + a callback.
     *   3. Round-trip the callback's signature to `/ads/adscore/verify`.
     *   4. Store the resulting verdict for `toTargeting()` / `shouldBlock()`.
     *
     * Resolves whether or not a verdict lands — never throws. Skips silently
     * when the server-provided config has no `apiKey`, which is how the
     * backend signals "AdScore off".
     */
    loadAndWait(config, apiClient, sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            if (!(config === null || config === void 0 ? void 0 : config.apiKey)) {
                return; // backend-controlled off-switch: no apiKey = AdScore inactive
            }
            if (!apiClient || !sessionId) {
                // Defensive: verify requires the session-bound backend call. Without
                // either half we cannot resolve a verdict, so fail-open.
                return;
            }
            if (typeof window === 'undefined' || typeof document === 'undefined') {
                return; // SSR / non-browser — silently skip
            }
            this.effectiveConfig = {
                mode: (_a = config.mode) !== null && _a !== void 0 ? _a : 'tag',
                blockedVerdicts: (_b = config.blockedVerdicts) !== null && _b !== void 0 ? _b : DEFAULT_BLOCKED_VERDICTS,
                timeoutMs: (_c = config.timeoutMs) !== null && _c !== void 0 ? _c : DEFAULT_TIMEOUT_MS,
            };
            try {
                const signature = yield this.waitWithTimeout(this.injectAndCaptureSignature(config.apiKey, (_d = config.subId) !== null && _d !== void 0 ? _d : DEFAULT_SUB_ID), this.effectiveConfig.timeoutMs);
                const { verdict } = yield apiClient.verifyAdscore(sessionId, signature);
                if (verdict)
                    this.verdict = verdict;
                this.loaded = true;
                console.log(`[GetJarAds] AdScore verdict: ${(_e = this.verdict) !== null && _e !== void 0 ? _e : 'unknown'} (mode: ${this.effectiveConfig.mode})`);
            }
            catch (err) {
                // Timeout, script load failure, callback error — fail open.
                console.warn('[GetJarAds] AdScore did not return a verdict in time, continuing without IVT filtering.', err);
            }
        });
    }
    /** Returns true when traffic is judged invalid AND mode is 'block'. */
    shouldBlock() {
        if (!this.effectiveConfig)
            return false;
        if (this.effectiveConfig.mode !== 'block')
            return false;
        if (!this.verdict)
            return false;
        return this.effectiveConfig.blockedVerdicts.includes(this.verdict);
    }
    /** Returns the AdScore verdict for inclusion as a GAM targeting key (empty if disabled / unknown). */
    toTargeting() {
        if (!this.loaded || !this.verdict)
            return {};
        return { adscore_verdict: this.verdict };
    }
    /**
     * Inject `//c.adsco.re`, call `AdscoreInit`, resolve with the opaque
     * signature returned by the async callback. Rejects on script load
     * failure, `callback.error !== 0`, or missing signature — the caller
     * treats every rejection as fail-open.
     */
    injectAndCaptureSignature(apiKey, subId) {
        return new Promise((resolve, reject) => {
            const w = window;
            const runInit = () => {
                if (typeof w.AdscoreInit !== 'function') {
                    reject(new Error('AdscoreInit not defined after script load'));
                    return;
                }
                w.AdscoreInit(apiKey, {
                    sub_id: subId,
                    async_callback: 1,
                    callback: (result) => {
                        var _a;
                        if (!result || result.error !== 0 || !result.signature) {
                            reject(new Error(`AdScore callback error=${(_a = result === null || result === void 0 ? void 0 : result.error) !== null && _a !== void 0 ? _a : 'unknown'}`));
                            return;
                        }
                        resolve(result.signature);
                    },
                });
            };
            // If `//c.adsco.re` is already on the page (e.g. dev reload), reuse it.
            if (typeof w.AdscoreInit === 'function') {
                runInit();
                return;
            }
            const script = document.createElement('script');
            script.src = ADSCORE_SCRIPT_URL;
            script.async = true;
            script.onload = runInit;
            script.onerror = () => reject(new Error('AdScore script failed to load'));
            document.head.appendChild(script);
        });
    }
    waitWithTimeout(p, ms) {
        return new Promise((resolve, reject) => {
            const t = setTimeout(() => reject(new Error(`AdScore timeout after ${ms}ms`)), ms);
            p.then((v) => {
                clearTimeout(t);
                resolve(v);
            }, (e) => {
                clearTimeout(t);
                reject(e);
            });
        });
    }
}
